import React, { FC, useState } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import Button from '@material-ui/core/Button';
import { Button as RaButton } from '~/components/ra';
import { Drawer, TextInput } from '~/components';
import SearchIcon from '@material-ui/icons/Search';
import { useFetchMenuItemById } from '~/hooks/menu';
import { MenuItem } from '~/types/Menu';
import Grid from '@material-ui/core/Grid';
import DetailsItemDrawer from './DetailsItemDrawer';
import { transformMenuItem } from '~/utils/menu';
import { makeStyles } from '@material-ui/core/styles';
import { useNotify } from '~/hooks';

const l = (key: string): string => `resources.menu.list.${key}`;

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    width: '450px',
    maxWidth: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1),
    gap: theme.spacing(1),
  },
  button: {
    height: '45px',
  },
  textInput: {
    flex: 1,
  },
}));

interface SearchDrawerProps {
  open: boolean;
  onClose: () => void;
}

const SearchDrawer: FC<SearchDrawerProps> = (props) => {
  const { open, onClose } = props;

  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const classes = useStyles();
  const { permissions } = usePermissions();
  const notify = useNotify();

  const handleResetState = () => {
    setItemId('');
    setItem(null);
  };

  const handleClose = () => {
    onClose();
    handleResetState();
  };

  const handleError = (error: any) => {
    notify({ message: error?.message, type: 'error' });
  };

  const { mutateAsync } = useFetchMenuItemById(handleError);

  const [itemId, setItemId] = useState('');
  const [item, setItem] = useState<MenuItem | null>(null);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setItemId(event.target.value);
  };

  const handleFindItem = async () => {
    setItem(null); // Reset item before fetching
    try {
      const item = await mutateAsync(itemId);
      setItem(item);
    } catch (error) {
      handleError(error);
    }
  };

  const handleRefetch = async () => {
    try {
      const item = await mutateAsync(itemId);
      setItem(item);
    } catch (error) {
      handleError(error);
    }
  };

  if (!permissions?.includes('dynamicMenu.view')) return null;

  return (
    <Drawer open={open} onClose={handleClose} heading={t('findItem')}>
      <Grid container>
        <Grid item xs={12} className={classes.searchContainer}>
          <TextInput
            label={t('itemId')}
            value={itemId}
            onChange={handleInputChange}
            className={classes.textInput}
          />
          <Button
            color='primary'
            size='small'
            startIcon={<SearchIcon />}
            onClick={handleFindItem}
            className={classes.button}
            disabled={!itemId.trim()}
          >
            {t('find')}
          </Button>
        </Grid>
        <Grid container item spacing={2} style={{ marginTop: '16px' }}>
          {item && (
            <Grid item xs={12}>
              <DetailsItemDrawer
                selectedNode={transformMenuItem(item, undefined, item?.parentId, false)}
                menuId={item?.menuId}
                refetch={handleRefetch}
                handleResetState={handleResetState}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Drawer>
  );
};

const SearchAction: FC = () => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { permissions } = usePermissions();
  if (!permissions?.includes('dynamicMenu.list')) return null;
  return (
    <>
      <RaButton label={t('findItem')} onClick={handleOpen} startIcon={<SearchIcon />} />
      <SearchDrawer open={open} onClose={handleClose} />
    </>
  );
};

export default SearchAction;
